import { useEffect, useRef } from "react";
import { authMessages } from "@/features/auth/messages";
import { formatIdleDuration } from "@/features/auth/session/formatIdleDuration";
import { formatRemainingSeconds } from "@/features/auth/session/formatRemainingSeconds";
import styles from "./SessionExpiryWarningModal.module.css";

type SessionExpiryWarningModalProps = {
  idleMs: number;
  remainingMs: number;
  onStaySignedIn: () => void;
  onSignOut: () => void;
};

export function SessionExpiryWarningModal({
  idleMs,
  remainingMs,
  onStaySignedIn,
  onSignOut,
}: SessionExpiryWarningModalProps) {
  const { sessionExpiry } = authMessages;
  const idleLabel = formatIdleDuration(idleMs);
  const remainingLabel = formatRemainingSeconds(remainingMs);
  const stayButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    stayButtonRef.current?.focus();
  }, []);

  return (
    <div
      className={styles.overlay}
      role="presentation"
      data-testid="session-expiry-modal-overlay"
    >
      <div
        className={styles.dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-expiry-title"
        aria-describedby="session-expiry-desc"
        data-testid="session-expiry-modal"
      >
        <h2 id="session-expiry-title" className={styles.title}>
          {sessionExpiry.title}
        </h2>
        <p id="session-expiry-desc" className={styles.body}>
          {sessionExpiry.body.beforeIdle}{" "}
          <strong data-testid="session-expiry-idle-duration">{idleLabel}</strong>
          {sessionExpiry.body.afterIdle}{" "}
          <strong data-testid="session-expiry-remaining">{remainingLabel}</strong>{" "}
          {sessionExpiry.body.afterRemaining}
        </p>
        <div className={styles.actions}>
          <button
            ref={stayButtonRef}
            type="button"
            className={styles.primaryBtn}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onStaySignedIn();
            }}
            data-testid="session-expiry-stay-signed-in"
          >
            {sessionExpiry.staySignedIn}
          </button>
          <button
            type="button"
            className={styles.secondaryBtn}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onSignOut();
            }}
            data-testid="session-expiry-sign-out"
          >
            {sessionExpiry.signOut}
          </button>
        </div>
      </div>
    </div>
  );
}
